import { TextFieldProps } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { DateTime } from 'luxon';
import { ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dataTestIds } from '../constants/data-test-ids';

export type CustomFormEventHandler = (event: React.FormEvent<HTMLFormElement>, value: any, field: string) => void;

interface DateSearchProps {
  date: DateTime | null;
  setDate: (dateTime: DateTime | null) => void;
  defaultValue?: DateTime | null;
  updateURL?: boolean;
  storeDateInLocalStorage?: boolean;
  label?: string;
  queryParams?: URLSearchParams;
  required?: boolean;
  disabled?: boolean;
  disableDates?: (day: DateTime) => boolean;
  closeOnSelect?: boolean;
  handleSubmit?: CustomFormEventHandler;
  small?: boolean;
  setIsValidDate?: (isValid: boolean) => void;
  'data-testid'?: string;
}

export default function DateSearch({
  date,
  setDate,
  defaultValue,
  updateURL,
  storeDateInLocalStorage,
  label,
  queryParams,
  required,
  disabled,
  disableDates,
  closeOnSelect,
  handleSubmit,
  small,
  setIsValidDate: setValidDate,
  'data-testid': dataTestId,
}: DateSearchProps): ReactElement {
  const [error, setError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const formatDate = typeof date === 'object' ? date?.toISODate() : date;
  const searchDate = queryParams?.get('searchDate') || formatDate;
  const navigate = useNavigate();
  useEffect(() => {
    if (updateURL && localStorage.getItem('selectedDate')) {
      queryParams?.set('searchDate', JSON.parse(localStorage.getItem('selectedDate') ?? '') ?? '');
      navigate(`?${queryParams?.toString()}`);
    }
  }, [navigate, queryParams, updateURL]);

  const handleDatePickerChange = (date: DateTime | null, event: any): void => {
    if (date === null) {
      setError(false);
      setErrorMessage('');
      setDate(null);
      if (setValidDate) {
        setValidDate(true);
      }
      if (storeDateInLocalStorage) {
        localStorage.removeItem('selectedDate');
      }
      if (handleSubmit) {
        handleSubmit(event, null, 'date');
      }
      return;
    }

    if (typeof date === 'object' && date?.toISODate()) {
      setError(false);
      setErrorMessage('');
      setDate(date);
      if (setValidDate) {
        setValidDate(true);
      }

      if (storeDateInLocalStorage) {
        localStorage.setItem('selectedDate', JSON.stringify(date.toISODate()));
      }

      if (handleSubmit) {
        handleSubmit(event, date, 'date');
      }
    } else {
      setErrorMessage('please enter date in format MM/DD/YYYY');
      setError(true);
      setDate(date);
      if (setValidDate) {
        setValidDate(false);
      }
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <DatePicker
        label={label ?? 'Date'}
        onChange={handleDatePickerChange}
        format={'MM/dd/yyyy'}
        slotProps={{
          textField: {
            style: { width: '100%' },
            required: required,
            error: error,
            helperText: errorMessage,
            name: 'date',
            id: 'appointment-date',
            label: label ?? 'Date',
            size: small ? 'small' : 'medium',
            'data-testid': dataTestId,
          } as TextFieldProps,
          actionBar: {
            actions: ['clear', 'today'],
            // @ts-expect-error - that's valid field
            'data-testid': dataTestIds.dashboard.datePickerTodayButton,
          },
        }}
        closeOnSelect={closeOnSelect}
        disabled={disabled}
        shouldDisableDate={disableDates}
        value={storeDateInLocalStorage ? (searchDate ? DateTime.fromISO(searchDate) : defaultValue) : date}
      />
    </LocalizationProvider>
  );
}
